import { ReactComponent as Logo } from "../images/logo-dark-text.svg";
import {
  CheckIcon,
  EyeIcon,
  EyeOffIcon,
  Loader2Icon,
  XIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { Button } from "@readouts/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@readouts/ui/form";
import { Input } from "@readouts/ui/input";
import { useEffect, useState } from "react";

type FormValues = {
  username: string;
  password: string;
  redirectUri: string;
};

export function Login() {
  const [showPw, setShowPw] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      username: "",
      password: "",
      redirectUri:
        new URLSearchParams(window.location.search).get("redirectUri") ??
        undefined,
    },
  });

  async function onSubmit(values: FormValues) {
    const res = await fetch("/login", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(values),
    });

    if (res.redirected) {
      window.location.href = res.url;
      return;
    }

    if (!res.ok) {
      form.setError("root", { message: "Neplatný email nebo heslo" });
      return;
    }
  }

  const [hostname, setHostname] = useState("readouts.cloud");

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.location.hostname === "localhost") {
        setHostname("localhost:3000");

        return;
      }
      setHostname(window.location.hostname.replace("identity.", ""));
    }
  }, []);

  return (
    <main className="w-screen h-dvh flex justify-center items-center bg-background relative">
      <title>Přihlašte se</title>

      <div className="w-96 bg-white rounded-2xl shadow-lg">
        <div className="p-5 flex flex-col justify-center gap-20 w-full">
          <div className="flex justify-center flex-col items-center gap-8">
            {/* <image src={logo} alt="Logo" width={150} height={56} /> */}
            <Logo width={150} height={56} />
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] uppercase tracking-wide text-slate-500">
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input type="email" className="w-full" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] uppercase tracking-wide text-slate-500">
                      Heslo
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPw ? "text" : "password"}
                          {...field}
                          placeholder={"••••••••"}
                          className={"h-9 pr-9 text-sm"}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw((p: boolean) => !p)}
                          className="absolute border-none right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPw ? (
                            <EyeOffIcon className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <input type="hidden" {...form.register("redirectUri")} />

              <div className="flex flex-col gap-2">
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? (
                    <Loader2Icon className="animate-spin text-white" />
                  ) : form.formState.isSubmitSuccessful ? (
                    <CheckIcon className="text-white" />
                  ) : form.formState.errors.root ? (
                    <XIcon className="text-white" />
                  ) : (
                    "Přihlásit se"
                  )}
                </Button>
                {form.formState.errors.root && (
                  <p className="text-sm text-red-500 text-center">
                    {form.formState.errors.root.message}
                  </p>
                )}
              </div>

              <div className="w-full flex text-xs items-center justify-center text-gray-400 gap-1.5">
                <div>
                  <p> Pokračováním do aplikace souhlasíte s </p>
                  <a
                    href={`https://${hostname}/vop/vseobecne_obchodni_podminky_v1.pdf`}
                    className="border-none underline"
                  >
                    Všeobecnými obchodními podmínkami
                  </a>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
      <span className="absolute bottom-2 text-xs text-muted-foreground">
        ©{new Date().getFullYear()} QONIQO s.r.o.
      </span>
    </main>
  );
}
